import React, { FC } from 'react'
import { Box, Flex } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import {
  useCoreLocaleText,
  LocaleTextHook,
} from '@moonpig/web-core-locale-text'
import { system as s } from '@moonpig/launchpad-system'
import { useLanguage } from '@moonpig/web-core-stores'
import { formatPrice } from '@moonpig/web-core-utils'
import { dictionary } from '@moonpig/web-core-locale-text/src/core-dictionary'

const StyledPriceContainer = styled(Flex)`
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: 8px;
`

const StyledPrice = styled.span<{ color?: string }>`
  ${({ color }) =>
    s({
      color,
    })};
`

const StyledFullPrice = styled.span<{ color?: string }>`
  ${({ color }) =>
    s({
      color: color ?? 'colorBlack70',
      typography: 'typeBodyCaption',
      fontWeight: 'bold',
    })};
`

const StyledDiscountDetails = styled.span`
  ${s({
    color: 'colorFeedbackSuccess',
    typography: 'typeBodyCaption',
    fontWeight: 'bold',
  })};
`

const getAriaLabel = (
  localiseText: LocaleTextHook<typeof dictionary>,
  showFromPrice: boolean,
  formattedPrice: string,
  formattedFullPrice: string | null,
  discountedPercentage: number | null,
) => {
  const hasDiscount = formattedFullPrice && discountedPercentage

  if (showFromPrice && hasDiscount) {
    return localiseText(
      'common.price_from_with_discount_explanation',
      formattedPrice,
      formattedFullPrice,
      discountedPercentage,
    )
  }

  if (showFromPrice) {
    return localiseText('common.price_from_explanation', formattedPrice)
  }

  if (hasDiscount) {
    return localiseText(
      'common.price_with_discount_explanation',
      formattedPrice,
      formattedFullPrice,
      discountedPercentage,
    )
  }

  return localiseText('common.price_explanation', formattedPrice)
}

export const ProductPrice: FC<{
  priceCentAmount: number
  currencyCode: string
  fractionDigits: number
  minimumQuantity?: number
  fullPriceCentAmount?: number
  discountedPercentage?: number | null
  description?: string
  showFromPrice?: boolean
  color?: string
}> = ({
  priceCentAmount,
  currencyCode,
  fractionDigits,
  fullPriceCentAmount,
  discountedPercentage,
  minimumQuantity,
  description,
  showFromPrice = false,
  color,
}) => {
  const localiseText = useCoreLocaleText()
  const language = useLanguage()
  const formattedPrice = formatPrice(
    priceCentAmount,
    fractionDigits,
    currencyCode,
    minimumQuantity,
    language,
  )
  const formattedFullPrice = fullPriceCentAmount
    ? formatPrice(
        fullPriceCentAmount,
        fractionDigits,
        currencyCode,
        minimumQuantity,
        language,
      )
    : null

  const explainPrice = getAriaLabel(
    localiseText,
    showFromPrice,
    formattedPrice,
    formattedFullPrice,
    discountedPercentage ?? null,
  )

  return (
    <Box
      typography={{ xs: 'typeMobileDisplay04', md: 'typeDesktopDisplay05' }}
      data-testid="product-price-with-description-text"
    >
      <StyledPriceContainer
        aria-label={explainPrice}
        data-testid="product-price-container"
      >
        <StyledPrice data-testid="product-price" color={color}>
          {showFromPrice
            ? `${localiseText('common.price_from')} ${formattedPrice}`
            : formattedPrice}
        </StyledPrice>
        {formattedFullPrice && !!discountedPercentage && (
          <>
            <StyledFullPrice
              color={color}
              data-testid="product-full-price"
              style={{
                textDecoration: 'line-through',
              }}
            >
              {formattedFullPrice}
            </StyledFullPrice>
            <StyledDiscountDetails data-testid="product-discount-details">
              {localiseText('common.discount_details', discountedPercentage)}
            </StyledDiscountDetails>
          </>
        )}
      </StyledPriceContainer>
      {description ? ` ${description}` : /* istanbul ignore next */ null}
    </Box>
  )
}
